import { ExternalLink, Globe, Youtube, Palette, Heart, Scale as ScaleIcon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { StarLogo } from "./StarLogo";

const ABOUT_LINKS = [
  { href: "https://www.sammystar.com", label: "Official Website", Icon: Globe },
  { href: "https://www.youtube.com/@sammystarart", label: "YouTube Channel", Icon: Youtube },
  { href: "https://sammystarart.etsy.com", label: "Art & Prints", Icon: Palette },
];

const SUPPORT_LINKS = [
  { href: "https://www.paypal.com/ncp/payment/C4LQGGSVCMV9E", label: "Donate via PayPal", sub: "Supports ongoing independent app development", Icon: Heart },
  { href: "https://fundrazr.com/a2k9b9", label: "Legal & Accountability Fund", sub: "Helps cover legal costs of administrative battles", Icon: ScaleIcon },
  { href: "https://sammystarart.etsy.com", label: "Collect Original Art & Prints", sub: "Direct from the artist", Icon: Palette },
];

function LinkRow({ href, label, sub, Icon }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer"
      className="group flex items-center gap-3 rounded-md border px-3 py-2.5 transition-colors hover:border-foreground hover:bg-muted/60"
      data-testid={`about-link-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}>
      <Icon className="h-4 w-4 shrink-0" />
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium truncate">{label}</span>
        {sub && <span className="block text-[11px] text-muted-foreground truncate">{sub}</span>}
      </span>
      <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground transition-colors group-hover:text-foreground" />
    </a>
  );
}

function SectionLabel({ children }) {
  return <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-primary">{children}</p>;
}

export default function AboutDialog({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="about-dialog">
        <DialogHeader className="space-y-3">
          <div className="flex items-center gap-3">
            <StarLogo className="h-8 w-8 shrink-0" />
            <DialogTitle className="font-heading text-2xl tracking-tight">About &amp; Support</DialogTitle>
          </div>
          <DialogDescription className="sr-only">About the creator, why StarVault was built, and ways to support independent creation.</DialogDescription>
        </DialogHeader>

        <section className="space-y-3 pt-2" data-testid="about-section-creator">
          <SectionLabel>About Sammy Star</SectionLabel>
          <p className="text-sm leading-relaxed text-muted-foreground">
            Sammy Star is an independent creator and visual artist presenting thought-provoking videos, installations, digital prints, and photography. Drawing on imagery from pop culture and everyday life, Sammy Star explores the gritty and the sleek, the provocative and the playful—while building practical, independent digital tools.
          </p>
          <div className="grid gap-2 sm:grid-cols-3">
            {ABOUT_LINKS.map((l) => <LinkRow key={l.href} {...l} />)}
          </div>
        </section>

        <div className="border-t" />

        <section className="space-y-3" data-testid="about-section-why">
          <SectionLabel>Why StarVault Was Created</SectionLabel>
          <p className="text-sm leading-relaxed text-muted-foreground">
            StarVault started out as a simple everyday notes app, but quickly evolved out of real-world necessity. When navigating complex disputes with building management, insurance claims, service deliveries, and health records, keeping track of scattered photos, receipts, and timelines across different apps was a nightmare.
          </p>
          <p className="text-sm leading-relaxed text-muted-foreground">
            StarVault was built to solve that problem—turning a basic notebook into a structured, offline-first vault capable of logging detailed incidents where every photo, note, and version is perfectly time-stamped as proof. It compiles these records into clean, chronological, easy-to-read PDF packages for official documentation. Every entry stays 100% on your own device with zero online storage fees or third-party tracking, giving you total control over your records when managing these ongoing legal battles.
          </p>
        </section>

        <div className="border-t" />

        <section className="space-y-3" data-testid="about-section-support">
          <SectionLabel>Support Independent Creation &amp; Advocacy</SectionLabel>
          <p className="text-sm leading-relaxed text-muted-foreground">
            StarVault is completely free to download and use—with zero subscriptions, no ads, and no storage fees. Contributions directly support ongoing independent app development and help cover the legal costs associated with fighting these administrative and institutional battles.
          </p>
          <div className="grid gap-2">
            {SUPPORT_LINKS.map((l) => <LinkRow key={l.label} {...l} />)}
          </div>
        </section>

        <p className="pt-2 text-center font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          StarVault • by Sammy Star • Private &amp; Offline
        </p>
      </DialogContent>
    </Dialog>
  );
}
